import React, { useEffect, useState } from "react";
import {
	IconButton,
	Tooltip,
	Typography,
	Link,
	Button,
	Grid,
	LinearProgress,
	Box,
} from "@mui/material";
import { useSelector } from "react-redux";

import { CommentIcon } from "utility/icons";
import { Shared } from "utility/shared";
import Emitter from "utility/emitter";
import ResponsiveDialog from "components/ResponsiveDialog";
import { RootState } from "redux/store";
import { IStepHistoryComment } from "models/StepHistoryDetail";
import { CustomTooltip } from "components/CustomTooltip";
import {
	IS_COREQUEST_ASSIGNED_TO_USER,
	OPEN_COREQUEST_STEP_HISTORY_DETAIL,
} from "utility/emitterEvents";

import { Loading } from "components/Loading";

import {
	ChangeOrderRequestStepHistoryEmitter,
	IChangeOrderRequestStepHistory,
} from "models/Budget/ChangeOrderRequest";
import { ChangeOrderRequestService } from "services/changeOrderRequest.service";
import { ChangeOrderRequestStepHistoryStatus } from "utility/enums/change-order-request";

type Props = {
	changeOrderRequestDetail: ChangeOrderRequestStepHistoryEmitter;
};

const ChangeOrderRequestStepsHistory: React.FC<Props> = ({
	changeOrderRequestDetail,
}) => {
	const userDetails = useSelector(
		(state: RootState) => state.authState.userDetails
	);
	const [
		changeOrderRequestStepHistoryList,
		setChangeOrderRequestStepHistoryList,
	] = useState<IChangeOrderRequestStepHistory[]>([]);
	const [openInfoDialog, setOpenInfoDialog] = useState<{
		message: string;
		open: boolean;
	}>({
		message: "",
		open: false,
	});
	const [activeStepIndex, setActiveStepIndex] = useState<number>(-1);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const isChangeOrderRequestStepAssignedToUser = (
		step: IChangeOrderRequestStepHistory
	) => {
		return step.assignToUsers && step.assignToUsers.length > 0
			? step.assignToUsers.findIndex((r) => r.userId === userDetails.userId) >
					-1
			: false;
	};

	const openChangeOrderRequestStepHistoryDetail = (
		item: IChangeOrderRequestStepHistory
	) => {
		Emitter.off(OPEN_COREQUEST_STEP_HISTORY_DETAIL, {} as any);
		Emitter.emit(OPEN_COREQUEST_STEP_HISTORY_DETAIL, item);
	};

	const setPrevStepAsSelected = (
		stepHistoryList: IChangeOrderRequestStepHistory[],
		filteredList: IChangeOrderRequestStepHistory[]
	) => {
		for (let i = 0; i < filteredList.length; i++) {
			const element = filteredList[i];
			if (isChangeOrderRequestStepAssignedToUser(element)) {
				const index = stepHistoryList.findIndex(
					(r) => r.changeOrderRequestStepId === element.changeOrderRequestStepId
				);
				if (index > -1) {
					setActiveStepIndex(index);
					openChangeOrderRequestStepHistoryDetail(stepHistoryList[index]);
					Emitter.emit(
						IS_COREQUEST_ASSIGNED_TO_USER,
						isChangeOrderRequestStepAssignedToUser(stepHistoryList[index])
					);
					break;
				}
			}
			// if no prev step is assigned to user, set first step as selected
			if (filteredList.length - 1 === i) {
				setActiveStepIndex(0);
				openChangeOrderRequestStepHistoryDetail(stepHistoryList[0]);
			}
		}
	};

	useEffect(() => {
		// get ChangeOrderRequestStepHistoryList
		if (changeOrderRequestDetail.changeOrderRequestId > -1) {
			setIsLoading(true);
			setActiveStepIndex(-1);
			ChangeOrderRequestService.getChangeOrderRequestStepList(
				changeOrderRequestDetail.changeOrderRequestId
			)
				.then((result) => {
					setChangeOrderRequestStepHistoryList(result);
					// groupWorkflowSteps(result);
					const index = result.findIndex(
						(r) =>
							r.changeOrderRequestStepId ===
							changeOrderRequestDetail.changeOrderRequestStepId
					);
					if (index > -1) {
						if (isChangeOrderRequestStepAssignedToUser(result[index])) {
							setActiveStepIndex(index);
							Emitter.emit(
								IS_COREQUEST_ASSIGNED_TO_USER,
								isChangeOrderRequestStepAssignedToUser(result[index])
							);
						} else {
							setPrevStepAsSelected(
								result,
								index > 0
									? JSON.parse(JSON.stringify(result)).slice(0, index).reverse()
									: JSON.parse(JSON.stringify(result)).reverse()
							);
						}
					} else {
						setPrevStepAsSelected(
							result,
							JSON.parse(JSON.stringify(result)).reverse()
						);
					}
					const activeElement = document.getElementById("active");
					if (activeElement) {
						activeElement.scrollIntoView({
							block: "center",
							behavior: "smooth",
						});
					}
					setIsLoading(false);
				})
				.catch((err) => {
					Shared.showSnackbarMessage(
						"Error loading changeOrderRequest step history list, please try again"
					);
					setIsLoading(false);
				});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		changeOrderRequestDetail.changeOrderRequestId,
		changeOrderRequestDetail.changeOrderRequestStepId,
	]);

	const getClassName = (status: string) => {
		let classes: string = "steps-root ";
		if (status === ChangeOrderRequestStepHistoryStatus.Ready) {
			classes += "in-progress";
		}
		if (status === ChangeOrderRequestStepHistoryStatus.Completed) {
			classes += "complete";
		}
		if (status === ChangeOrderRequestStepHistoryStatus.Recalled) {
			classes += "recall";
		}
		if (status === ChangeOrderRequestStepHistoryStatus.ChangeRequested) {
			classes += "change-request";
		}
		if (status === ChangeOrderRequestStepHistoryStatus.Rejected) {
			classes += "reject";
		}
		if (status === ChangeOrderRequestStepHistoryStatus.Deleted) {
			classes += "delete";
		}
		if (status === ChangeOrderRequestStepHistoryStatus.Delegated) {
			classes += "delegate";
		}
		if (status === ChangeOrderRequestStepHistoryStatus.Escalated) {
			classes += "escalate";
		}
		if (status === ChangeOrderRequestStepHistoryStatus.Cancelled) {
			classes += "cancel";
		}
		return classes;
	};

	const handleStepDetailClick = (
		item: IChangeOrderRequestStepHistory,
		index: number
	) => {
		if (activeStepIndex === index) {
			return;
		}
		if (
			item.stepStatus === ChangeOrderRequestStepHistoryStatus.Pending ||
			(item.stepStatus === ChangeOrderRequestStepHistoryStatus.Ready &&
				!isChangeOrderRequestStepAssignedToUser(item))
		) {
			setOpenInfoDialog({
				open: true,
				message: `The step selected ${item.stepNumber}, has not been completed and there is no information to display for the step.`,
			});
		} else {
			setActiveStepIndex(index);
			openChangeOrderRequestStepHistoryDetail(item);
			Emitter.emit(
				IS_COREQUEST_ASSIGNED_TO_USER,
				isChangeOrderRequestStepAssignedToUser(item)
			);
		}
	};

	// const groupWorkflowSteps = (steps: IChangeOrderRequestStepHistory[]) => {
	// 	const grouped: { [key: string]: IChangeOrderRequestStepHistory[] } = {};

	// 	steps.forEach((step) => {
	// 		const groupName =
	// 			Object.entries(
	// 				ChangeOrderRequestManagementUtil.workflowGroupsConfig
	// 			).find(([, ids]) => ids.includes(step.modulePhaseId))?.[0] ||
	// 			"Other Workflow";

	// 		if (!grouped[groupName]) grouped[groupName] = [];
	// 		grouped[groupName].push(step);
	// 	});

	// 	return Object.entries(grouped).map(([type, steps]) => ({
	// 		type,
	// 		steps,
	// 	}));
	// };

	return (
		<div className="history-panel">
			{/* {changeOrderRequestStepHistoryList.length > 0 &&
				groupWorkflowSteps(changeOrderRequestStepHistoryList).map(
					({ type, steps }, groupIndex) => ( */}
			<div>
				<div className="heading-part">
					<Typography variant="h4">
						Budget Change Order Request Steps - History
					</Typography>
					{changeOrderRequestStepHistoryList.length > 0 && (
						<div className="stepper-wrapper">
							<Typography variant="caption">
								{`${changeOrderRequestStepHistoryList[0].stepCompletedInPercentage}% Completed`}
							</Typography>
							<Box sx={{ ml: 1, maxWidth: 400, flexGrow: 1 }}>
								<LinearProgress
									variant="determinate"
									value={
										changeOrderRequestStepHistoryList[0]
											.stepCompletedInPercentage
									}
								/>
							</Box>
						</div>
					)}
				</div>
				<div className="steps-wrapper">
					{!changeOrderRequestStepHistoryList.length &&
						(isLoading ? (
							<Loading isWorkflowStepLoader={true} />
						) : (
							<Typography
								className="steps-root"
								variant="body1"
								justifyContent="center"
							>
								No Data Available
							</Typography>
						))}
					{changeOrderRequestStepHistoryList.length > 0 &&
						changeOrderRequestStepHistoryList.map(
							(item: IChangeOrderRequestStepHistory, index: number) => (
								<Link
									key={index}
									onClick={(e) => {
										e.preventDefault();
										handleStepDetailClick(item, index);
									}}
									underline="none"
									className={`${getClassName(item.stepStatus)} ${
										index === activeStepIndex && "active"
									}`}
									id={index === activeStepIndex ? "active" : undefined}
									style={{
										cursor: "pointer",
									}}
								>
									<Tooltip title={item.stepStatus} placement="bottom">
										<div className="requisition-steps-icon">
											{item.stepNumber.includes(".0")
												? item.stepNumber.split(".")[0]
												: item.stepNumber}
										</div>
									</Tooltip>
									<div className="step-content">
										<Typography variant="body2">{item.description}</Typography>
										<>
											<Typography variant="caption">
												{item.assignToUsers.map((user, index) => (
													<span
														key={index}
														className={
															user.isTaskCompleted ? "green-text fw-500" : ""
														}
													>{`${user.fullName}${
														index !== item.assignToUsers.length - 1 ? ", " : ""
													}`}</span>
												))}
											</Typography>
											<div className="step-content-middle">
												<div className="left-part">
													<Typography variant="caption" className="light">
														Duration
													</Typography>
													<Typography variant="caption">
														{item.duration || "-"}
													</Typography>
												</div>
												<div className="right-part">
													<Typography variant="caption" className="light">
														Avg. Duration
													</Typography>
													<Typography variant="caption">
														{item.averageDuration || "-"}
													</Typography>
												</div>
											</div>
										</>
										<div className="step-content-bottom">
											<div className="left-part">
												<Tooltip
													title={
														item.completedDate
															? `Completed by ${item.completedBy}`
															: ""
													}
													arrow
													placement="top"
												>
													<div>
														<Typography variant="caption" className="light">
															Date Completed
														</Typography>
														<Typography variant="caption">
															{item.completedDate
																? `${Shared.getFormattedDate(
																		new Date(item.completedDate),
																		"dd/MM/yyyy"
																  )} - ${Shared.getFormattedDate(
																		new Date(item.completedDate),
																		"hh:mmaaa"
																  )}`
																: "-"}
														</Typography>
													</div>
												</Tooltip>

												{/* {type === "Hold Workflow" ||
												type === "Termination Workflow" ? (
													<ChangeOrderRequestAttachment
														changeOrderRequestId={
															changeOrderRequestDetail.changeOrderRequestId
														}
														isDisabled={
															!changeOrderRequestDetail.changeOrderRequestId ||
															!changeOrderRequestDetail.changeOrderRequestStepId ||
															item.stepStatus ===
																ChangeOrderRequestStepStatus.Pending
														}
														changeOrderRequestStepId={
															changeOrderRequestDetail.changeOrderRequestStepId ||
															0
														}
														isAttachmentViewOnly={
															isChangeOrderRequestStepAssignedToUser(item) &&
															item.stepStatus ===
																ChangeOrderRequestStepStatus.Ready
																? false
																: true
														}
														stepNumber={item.stepNumber}
														modulePhaseId={item.modulePhaseId}
													/>
												) : (
													<></>
												)} */}
											</div>

											{item.comments.length > 0 && (
												<CustomTooltip
													tooltipStyle={{
														border: "1px solid #034f8b",
													}}
													tooltipProps={{
														title: item.comments.map(
															(cmt: IStepHistoryComment, index: number) => (
																<Grid
																	container
																	key={index}
																	className={
																		index !== 0 ? "history-comment" : ""
																	}
																>
																	<Grid item xs={12} className="fw-500">
																		{cmt.fullName}
																	</Grid>
																	<Grid item xs={12} mt={1}>
																		{cmt.comment}
																	</Grid>
																</Grid>
															)
														),
														className: "history-tooltip",
														placement: "right",
														children: (
															<IconButton>
																<CommentIcon />
															</IconButton>
														),
													}}
												/>
											)}
										</div>
									</div>
								</Link>
							)
						)}
				</div>
			</div>
			{/* )
				)} */}

			{openInfoDialog.open && (
				<ResponsiveDialog
					open={openInfoDialog.open}
					title="Step History Info"
					showActionButtons={false}
					titleCloseIcon={false}
					maxWidth="xs"
					onCancel={() => {}}
				>
					<Grid container spacing={1}>
						<Grid item xs={12}>
							<Typography variant="h4" color="black">
								{openInfoDialog.message}
							</Typography>
						</Grid>
						<Grid
							item
							xs={12}
							textAlign="center"
							sx={{ paddingTop: "24px !important" }}
						>
							<Button
								variant="contained"
								onClick={() => {
									setOpenInfoDialog({ open: false, message: "" });
								}}
							>
								Close
							</Button>
						</Grid>
					</Grid>
				</ResponsiveDialog>
			)}
		</div>
	);
};

export default ChangeOrderRequestStepsHistory;
